import { useEffect, useState } from "react";
import { Rating } from "@smastrom/react-rating";
import "@smastrom/react-rating/style.css";
import Avatar from "./Avatar";
import { useStore } from "../store";
import { Utils } from "../utils";
import { useSellerPublicReviews } from "../hooks/queries";

interface Props {
  sellerId: string;
}

const PAGE_SIZE = 5;

function SellerReviews({ sellerId }: Props) {
  const { setErrorMessage } = useStore((s: any) => s);
  const [page, setPage] = useState(0);

  const { data, isLoading, error } = useSellerPublicReviews(
    sellerId,
    page,
    PAGE_SIZE
  );
  const list: any[] = data?.list || [];
  const count: number = data?.count || 0;
  const isReady = !isLoading;

  useEffect(() => {
    if (error) setErrorMessage((error as Error).message);
  }, [error, setErrorMessage]);

  if (isReady && list.length === 0) return null;

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <section className="sellerReviews">
      <div className="sellerReviews__header">
        <h2 className="sellerReviews__title">Reviews</h2>
        {count > 0 && (
          <span className="sellerReviews__count">
            {count} review{count === 1 ? "" : "s"}
          </span>
        )}
      </div>

      <div className="sellerReviews__list">
        {list.map((r) => (
          <article key={r._id} className="sellerReviewCard">
            <header className="sellerReviewCard__header">
              <Avatar
                src={r?.buyerId?.fileUrl}
                name={r?.buyerId?.name}
                className="sellerReviewCard__avatar"
              />
              <div className="sellerReviewCard__identity">
                <div className="sellerReviewCard__author">
                  {r?.buyerId?.name}
                </div>
                <div className="sellerReviewCard__meta">
                  <Rating
                    style={{ maxWidth: 84 }}
                    value={r.rating}
                    readOnly
                  />
                  <span className="sellerReviewCard__date">
                    {Utils.formatDate(r.createdAt)}
                  </span>
                </div>
              </div>
            </header>

            {r.text && <p className="sellerReviewCard__body">{r.text}</p>}

            {r?.sellerResponse?.text && (
              <div className="sellerReviewCard__response">
                <div className="sellerReviewCard__responseLabel">
                  <i
                    className="bi bi-arrow-return-right"
                    aria-hidden="true"
                  />
                  Seller's response ·{" "}
                  {Utils.formatDate(r.sellerResponse.respondedAt)}
                </div>
                <p className="sellerReviewCard__responseText">
                  {r.sellerResponse.text}
                </p>
              </div>
            )}
          </article>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="sellerReviews__pager">
          <button
            type="button"
            className="sellerReviews__pagerBtn"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            aria-label="Previous page"
          >
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Previous
          </button>
          <span className="sellerReviews__pagerInfo">
            Page {page + 1} of {totalPages}
          </span>
          <button
            type="button"
            className="sellerReviews__pagerBtn"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage(page + 1)}
            aria-label="Next page"
          >
            Next
            <i className="bi bi-arrow-right" aria-hidden="true" />
          </button>
        </div>
      )}
    </section>
  );
}

export default SellerReviews;
